import { useEffect, useState } from 'react';
import { GOAL_STEPS, GENDER, WEIGHT_UNIT } from '../../../types/enum';
import { convertWeight } from '../../../lib/utils';

const getGoalOptions = (goal, calories) => {
  if (goal === 'Lose weight') {
    const least = Math.floor(calories - 250);
    const medium = Math.floor(calories - 500);
    const most = Math.floor(calories - 1000);
    return [
      { key: least, value: `${least} Cal/day,-0.25kg per week`, weight: -0.25 },
      { key: medium, value: `${medium} Cal/day,-0.5kg per week`, weight: -0.5 },
      { key: most, value: `${most} Cal/day,-1kg per week`, weight: -1 },
    ];
  }
  if (goal === 'Maintain') {
    return [{ key: calories, value: `${calories} (maintain)`, weight: 0 }];
  }
  if (goal === 'Gain weight') {
    const least = Math.floor(calories + 250);
    const medium = Math.floor(calories + 500);
    const most = Math.floor(calories + 1000);
    return [
      { key: least, value: `${least} Cal/day,+0.25kg per week`, weight: 0.25 },
      { key: medium, value: `${medium} Cal/day,+0.5kg per week`, weight: 0.5 },
      { key: most, value: `${most} Cal/day,+1kg per week`, weight: 1 },
    ];
  }
};

const activities = [
  {
    key: 'sedentary',
    value: 1.2,
    name: 'sedentary',
    label: 'None (little or no exercise)',
    min: 0,
    max: 30,
  },
  {
    key: 'lightlyActive',
    value: 1.375,
    name: 'lightlyActive',
    label: 'Light (1-4 days exercise or sports per week)',
    min: 31,
    max: 150,
  },
  {
    key: 'moderately',
    value: 1.55,
    name: 'moderately',
    label: 'Some (5-7 days exercise or sports per week)',
    min: 151,
    max: 300,
  },
  {
    key: 'veryActive',
    value: 1.725,
    name: 'veryActive',
    label: 'Hard (everyday exercise or exercise 2 times per day)',
    min: 301,
    max: 450,
  },
  {
    key: 'extraActive',
    value: 1.9,
    name: 'extraActive',
    label: 'Extra Hard (exercise 2 or more times per day)',
    min: 451,
    max: Infinity,
  },
];

const getUserActivityLevel = (mins) => {
  for (const act of activities) {
    if (mins >= act.min && mins <= act.max) {
      return act.value;
    }
  }
  return 1.2;
};

const calculateBMI = (height, weight) => {
  const cm = height;
  const kg = weight;
  return cm && kg ? Math.round((kg / (cm / 100) ** 2) * 100) / 100 : 0;
};

const calculateBMR = ({ height, weight, age, gender }) => {
  const cm = height;
  const kg = weight;

  if (cm && kg && age && age > 17) {
    let metric = Math.round((10 * kg + 6.25 * cm - 5 * age) * 100) / 100;
    return gender === GENDER.MALE ? metric - 5 : metric - 161;
  }

  return 0;
};

const calculateCaloriePerDay = (bmr, activity) => {
  return Math.round(bmr * activity * 100) / 100;
};

const calculateProteinPerDay = ({ bmi, weight, activity }) => {
  const kg = weight;
  const from = Math.round(kg * 1.2 * 100) / 100;
  const to = Math.round(kg * 1.5 * 100) / 100;
  const range = [from, to];

  if (bmi > 24.9) {
    return range;
  }

  if ([1.2, 1.375].includes(activity)) {
    range[1] = Math.round(kg * 1.8 * 100) / 100;
  }
  if ([1.55, 1.725].includes(activity)) {
    range[1] = Math.round(kg * 2.0 * 100) / 100;
  }
  if (activity === 1.9) {
    range[1] = Math.round(kg * 2.4 * 100) / 100;
  }

  return range;
};

const DailyCalorie = ({
  goals,
  setGoals,
  setCurrentStep,
  userDetail,
  weightUnit,
}) => {
  const [calculation, setCalculation] = useState({
    bmi: 0,
    bmr: 0,
    calories: 0,
    proteins: [0, 0],
  });

  useEffect(() => {
    const { height, weight, gender, birth } = userDetail;

    const weightInKg =
      weightUnit === WEIGHT_UNIT.LB
        ? convertWeight(weight, WEIGHT_UNIT.LB, WEIGHT_UNIT.KG)
        : weight;

    const activity = getUserActivityLevel(
      Number(goals.workoutHours) * Number(goals.workoutDays)
    );
    const [year, month] = birth.split('-');
    let age = new Date().getFullYear() - Number(year);
    if (parseInt(month) < new Date().getMonth() + 1) {
      age--;
    }

    const BMI = calculateBMI(height, weightInKg);
    const BMR = calculateBMR({
      height,
      weight: weightInKg,
      age,
      gender,
    });
    const dailyCalories = calculateCaloriePerDay(BMR, activity);
    const dailyProteins = calculateProteinPerDay({
      bmi: BMI,
      weight: weightInKg,
      activity,
    });

    setGoals((prev) => ({
      ...prev,
      TDEE: dailyCalories,
    }));

    setCalculation({
      bmi: BMI,
      bmr: BMR,
      calories: dailyCalories,
      proteins: dailyProteins,
    });
  }, []);

  const handleInput = (key) => () => {
    const options = getGoalOptions(goals.endGoal, calculation.calories);
    const target = options.find((option) => option.key === key);
    if (target?.key) {
      target.weight =
        weightUnit === WEIGHT_UNIT.LB
          ? convertWeight(target.weight, WEIGHT_UNIT.KG, WEIGHT_UNIT.LB)
          : target.weight;
      setGoals((prev) => ({ ...prev, dailyCalorie: target.key }));
    }
  };

  const handleInputChange = (e) => {
    const value = Number(e.target.value);
    setGoals((prev) => ({ ...prev, ownCalorie: value }));
  };

  const handleNext = () => {
    try {
      setCurrentStep(GOAL_STEPS.DAILY_PCF);
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div>
      <div className='mb-6'>
        <h1 className='font-bold mb-3 text-white text-2xl'>
          Your Body Profile
        </h1>
        <div className='bg-white/10 rounded-lg p-4 space-y-3'>
          <div className='flex items-center'>
            <div className='text-white/80 text-sm flex-1'>
              BMI
              <br />
              <span className='text-white/60 text-xs'>Body Mass Index</span>
            </div>
            <div className='text-white font-bold'>{calculation.bmi}</div>
          </div>
          <div className='flex items-center'>
            <div className='text-white/80 text-sm flex-1'>
              BMR
              <br />
              <span className='text-white/60 text-xs'>
                Basal Metabolic Rate
              </span>
            </div>
            <div className='text-white font-bold'>
              {calculation.bmr.toLocaleString()} Cal/day
            </div>
          </div>
          <div className='flex items-center'>
            <div className='text-white/80 text-sm flex-1'>
              TDEE
              <br />
              <span className='text-white/60 text-xs'>
                Total Daily Energy Expenditure
              </span>
            </div>
            <div className='text-white font-bold'>
              {calculation.calories.toLocaleString()} Cal/day
            </div>
          </div>
        </div>
      </div>
      <h1 className='font-bold mb-3 text-white text-2xl leading-tight'>
        How many calories would you like to consume daily?
      </h1>
      <div className='space-y-2'>
        {getGoalOptions(goals.endGoal, calculation.calories).map((option) => (
          <div key={option.key}>
            <button
              className={`px-3 py-2 w-full rounded-full ${
                goals.dailyCalorie === option.key
                  ? 'bg-yellow-500 text-black'
                  : 'bg-white text-black'
              }`}
              onClick={handleInput(option.key)}
            >
              <div className='text-black/80 text-sm font-bold'>
                {option.value.split(',')[0]}
              </div>
              <div className='text-black/60 text-xs'>
                {weightUnit === WEIGHT_UNIT.LB
                  ? `${convertWeight(
                      option.weight,
                      WEIGHT_UNIT.KG,
                      WEIGHT_UNIT.LB
                    ).toFixed(2)} lb`
                  : `${option.weight} kg`}{' '}
                per week
              </div>
            </button>
          </div>
        ))}
      </div>
      {(goals.endGoal === 'Lose weight' || goals.endGoal === 'Gain weight') && (
        <div className='mt-3'>
          <label className='text-white text-sm font-bold'>
            You can set your own value, if you want.
          </label>
          <div className='flex items-center gap-2'>
            <input
              type='number'
              className='w-full bg-white rounded-full py-2 px-4 '
              value={goals.ownCalorie}
              onChange={handleInputChange}
            />
            <div className='text-white text-sm font-bold'>Cal/day</div>
          </div>
        </div>
      )}
      <button
        className={`w-full bg-yellow-500 text-black font-bold rounded-full hover:bg-yellow-600 transition-colors duration-200 py-3 mt-5 ${
          !goals.dailyCalorie && !goals.ownCalorie
            ? 'opacity-40'
            : 'hover:bg-yellow-600 transition-colors duration-200'
        }`}
        onClick={handleNext}
        disabled={!goals.dailyCalorie && !goals.ownCalorie}
      >
        Next
      </button>
    </div>
  );
};

export default DailyCalorie;
